// User Profile API
// Ensures every Cognito user has a corresponding KV profile with stats.
// The "ensure-profile" endpoint is idempotent — safe to call on every login.

import { API_BASE, getAuthHeaders, apiFetch } from './api-fetch';
import { API, buildPath } from './api-endpoints';

export interface UserProfile {
  id: string;
  cognitoUsername: string;
  name: string;
  email: string;
  role: string;
  status: string;
  phone: string;
  avatar: string | null;
  lastActive: string;
  createdAt: string;
  updatedAt: string;
  permissions: string[];
  properties: number;
  clients: number;
  revenue: number;
  region: string;
  propertiesManaged: number;
  clientsManaged: number;
  salesCompleted: number;
  commission: number;
  commissionRate?: number;
  lastLogin: string;
  joinDate: string;
  authProvider: string;
  department?: string;
  bio?: string;
  cognitoGroups?: string[];
  rating?: number;
  propertiesSold?: number;
  yearsExperience?: number;
}

/**
 * Ensure a user profile exists in the KV store.
 * If the profile already exists, bumps lastLogin and returns it.
 * If it doesn't exist, creates one with all stats initialised to 0.
 *
 * This should be called after every successful Cognito login or registration
 * to keep the KV profile in sync with Cognito.
 */
export async function ensureUserProfile(data: {
  id: string;
  email: string;
  name?: string;
  role?: string;
  groups?: string[];
  phone?: string;
  cognitoUsername?: string;
  region?: string;
  department?: string;
  bio?: string;
}): Promise<{ user: UserProfile; created: boolean }> {
  try {
    // Spec: POST /users/profile/ensure
    const response = await apiFetch(`${API_BASE}${API.users.ensureProfile.path}`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
      signal: AbortSignal.timeout(8000) // 8 second timeout
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      console.error('[UserProfile] ensure-profile failed:', response.status, err);
      throw new Error(err.error || `Failed to ensure profile (${response.status})`);
    }

    const result = await response.json();

    if (result.created) {
      console.log(`[UserProfile] ✅ Created new profile for ${data.email} (${result.user.role})`);
    } else {
      console.log(`[UserProfile] ✅ Profile exists for ${data.email}, updated lastLogin`);
    }

    return { user: result.user, created: result.created };
  } catch (error: any) {
    // More user-friendly error logging
    if (error.name === 'TimeoutError' || error.name === 'AbortError') {
      console.warn('[UserProfile] ⏱️ Profile sync timed out - backend may be sleeping (non-critical)');
    } else if (error.message === 'Failed to fetch') {
      console.warn('[UserProfile] 🔌 Backend unavailable - profile sync skipped (non-critical)');
    } else {
      console.error('[UserProfile] ❌ Error in ensureUserProfile:', error.message || error);
    }
    throw error;
  }
}

/**
 * Get a user's KV profile. Returns null if no profile exists.
 */
export async function getUserProfile(id: string): Promise<UserProfile | null> {
  try {
    // Spec: GET /users/profile (authenticated user's own profile)
    const response = await apiFetch(`${API_BASE}${API.users.getProfile.path}`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      console.error('[UserProfile] get profile failed:', response.status);
      return null;
    }

    const data = await response.json();
    return data.profile || data.data?.profile || data.user || null;
  } catch (error) {
    console.error('[UserProfile] Error fetching profile:', error);
    return null;
  }
}

/**
 * Update a user's KV profile with partial data.
 * Only provided fields are updated; others are preserved.
 */
export async function updateUserProfile(
  id: string,
  updates: Record<string, any>,
): Promise<UserProfile | null> {
  try {
    // Spec: PUT /users/profile
    const response = await apiFetch(
      `${API_BASE}${API.users.updateProfile.path}`,
      {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(updates),
      },
    );

    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      console.error('[UserProfile] update profile failed:', response.status, err);
      throw new Error(err.error || `Failed to update profile (${response.status})`);
    }

    const data = await response.json();
    return data.user || null;
  } catch (error) {
    console.error('[UserProfile] Error updating profile:', error);
    throw error;
  }
}

/**
 * Upload a profile photo for a user.
 * Converts the file to base64 and sends it to the server which stores it in Supabase Storage.
 */
export async function uploadProfilePhoto(
  userId: string,
  file: File,
): Promise<{ url: string; path: string }> {
  try {
    // Convert file to base64 data URL
    const reader = new FileReader();
    const base64Data = await new Promise<string>((resolve, reject) => {
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

    const response = await apiFetch(
      `${API_BASE}${API.users.uploadPhoto.path}`,
      {
        method: 'POST',
        headers: getAuthHeaders(),
        // Spec: POST /users/profile/photo requires { imageBase64, contentType }
        body: JSON.stringify({
          imageBase64: base64Data,
          contentType: file.type,
        }),
      },
    );

    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      console.error('[UserProfile] upload photo failed:', response.status, err);
      throw new Error(err.error || `Failed to upload photo (${response.status})`);
    }

    const data = await response.json();
    // Spec envelope: { success, data: { url, path, ... } }
    const photoData = data?.data ?? data;
    return { url: photoData.url, path: photoData.path };
  } catch (error) {
    console.error('[UserProfile] Error uploading profile photo:', error);
    throw error;
  }
}